'use client';

import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import {
  resolveSequentialSteps,
  sequentialStepIndex,
  type SequentialStepInput,
  type SequentialStepState,
} from 'lib/buy/sequentialSteps';

type StepRef = RefObject<HTMLDivElement | null>;

export type SequentialStep<Id extends string> = SequentialStepInput<Id> & {
  ref?: StepRef;
  /** Completed by the system, not the shopper: when this step's completion
   * advances the flow, skip the scroll, the announcement and onStepCompleted. */
  quiet?: boolean;
};

export type UseSequentialStepsOptions<Id extends string> = {
  steps: SequentialStep<Id>[];
  /** Brings a freshly unlocked step into view. */
  scrollTo?: (ref: StepRef) => void;
  /** Draws attention to the active step when the shopper presses Continue. */
  highlight?: (ref: StepRef) => void;
  onStepCompleted?: (id: Id) => void;
  onStepUnlocked?: (id: Id) => void;
  /** Lets the unlocked section lay out before the page scrolls to it. */
  scrollDelayMs?: number;
};

export type SequentialStepsApi<Id extends string> = {
  activeStepId: Id | null;
  allComplete: boolean;
  stepStateOf: (id: Id) => SequentialStepState;
  advance: () => void;
  /** Scrolls to the first unanswered step after a re-answer that leaves it unchanged. */
  scrollToActive: () => void;
};

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number']);

let stepLive: HTMLElement | null = null;

function announceStep(message: string) {
  if (typeof document === 'undefined') return;
  if (!stepLive) {
    stepLive = document.createElement('div');
    stepLive.setAttribute('role', 'status');
    stepLive.setAttribute('aria-live', 'polite');
    stepLive.style.position = 'absolute';
    stepLive.style.width = '1px';
    stepLive.style.height = '1px';
    stepLive.style.padding = '0';
    stepLive.style.margin = '-1px';
    stepLive.style.overflow = 'hidden';
    stepLive.style.clip = 'rect(0, 0, 0, 0)';
    stepLive.style.whiteSpace = 'nowrap';
    stepLive.style.border = '0';
    document.body.appendChild(stepLive);
  }
  stepLive.textContent = '';
  window.setTimeout(() => {
    if (stepLive) stepLive.textContent = message;
  }, 30);
}

function isTypingInField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

export function useSequentialSteps<Id extends string>({
  steps,
  scrollTo,
  highlight,
  onStepCompleted,
  onStepUnlocked,
  // Outlasts the 0.3s manual-size accordion collapse: measuring mid-collapse
  // overshoots the next step (e.g. "I know my size" -> sizing kit lands on MAX).
  scrollDelayMs = 350,
}: UseSequentialStepsOptions<Id>): SequentialStepsApi<Id> {
  const signature = steps
    .map(
      (s) =>
        `${s.id}:${
          s.visible === false ? 'h' : s.terminal ? 't' : s.complete ? '1' : '0'
        }`,
    )
    .join('|');
  const resolution = useMemo(() => resolveSequentialSteps(steps), [signature]);

  const latest = useRef({
    steps,
    scrollTo,
    highlight,
    onStepCompleted,
    onStepUnlocked,
  });
  latest.current = {
    steps,
    scrollTo,
    highlight,
    onStepCompleted,
    onStepUnlocked,
  };
  const previousActive = useRef<Id | null | undefined>(undefined);
  const activeRef = useRef(resolution.activeId);
  activeRef.current = resolution.activeId;
  const scrollTimer = useRef<number | undefined>(undefined);

  // One pending scroll at a time; the target is read when it fires, so a
  // re-answer and the unlock effect below always agree on where to land.
  const scheduleScroll = useCallback(() => {
    window.clearTimeout(scrollTimer.current);
    scrollTimer.current = window.setTimeout(() => {
      const current = latest.current;
      const ref = current.steps.find((s) => s.id === activeRef.current)?.ref;
      if (ref) current.scrollTo?.(ref);
    }, scrollDelayMs);
  }, [scrollDelayMs]);

  useEffect(() => () => window.clearTimeout(scrollTimer.current), []);

  useEffect(() => {
    const prev = previousActive.current;
    const next = resolution.activeId;
    previousActive.current = next;
    if (prev === undefined || prev === next) return;

    const current = latest.current;
    // Re-opening an earlier step (clearing a choice) must never yank the page.
    if (
      sequentialStepIndex(current.steps, next) <=
      sequentialStepIndex(current.steps, prev)
    ) {
      return;
    }
    // A step the system completed (not the shopper) moves on silently.
    const quiet =
      prev !== null && current.steps.find((s) => s.id === prev)?.quiet === true;
    if (prev !== null && !quiet) current.onStepCompleted?.(prev);
    if (next === null) return;
    current.onStepUnlocked?.(next);
    if (quiet) return;
    const heading = current.steps
      .find((s) => s.id === next)
      ?.ref?.current?.querySelector('h5')
      ?.textContent?.replace(/\s+/g, ' ')
      .trim();
    if (heading) announceStep(heading);

    // A step completed by typing (the engraving text) unlocks the next one
    // but must not scroll the field away mid-word; Continue still gets there.
    if (isTypingInField()) return;
    scheduleScroll();
  }, [resolution.activeId, scheduleScroll]);

  const stepStateOf = useCallback(
    // Unknown ids resolve to complete so a wiring miss can never block checkout.
    (id: Id): SequentialStepState => resolution.states[id] ?? 'complete',
    [resolution],
  );

  const advance = useCallback(() => {
    const current = latest.current;
    const ref = current.steps.find((s) => s.id === resolution.activeId)?.ref;
    if (ref && current.highlight) current.highlight(ref);
  }, [resolution.activeId]);

  return {
    activeStepId: resolution.activeId,
    allComplete: resolution.allComplete,
    stepStateOf,
    advance,
    scrollToActive: scheduleScroll,
  };
}
